import { BusinessStatusBadge } from "@nwga/features/businesses/ui";
import { useTRPC } from "@nwga/features/client";
import { Button } from "@nwga/ui/components/button";
import { EmptyState } from "@nwga/ui/components/empty-state";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { ArrowRightIcon, BuildingIcon, PlusIcon } from "lucide-react";
import { BusinessAvatar } from "../../components/business-switcher.tsx";
import { Container } from "../../components/container.tsx";
import { PageHeader } from "../../components/page-header.tsx";

/**
 * Choosing which business to work on.
 *
 * Most people here belong to exactly one, and for them this page is pure
 * friction — a list of one thing, then a click to get where they were always
 * going. So they never see it: `beforeLoad` sends them straight into the
 * workspace, and the switcher in its sidebar is how they would get back out if
 * they ever acquired a second.
 *
 * It stays a real page rather than becoming a redirect-only route because the
 * other two cases are real too: nobody has registered anything yet, or somebody
 * manages several and needs to pick.
 */
export const Route = createFileRoute("/dashboard/")({
	beforeLoad: async ({ context }) => {
		const memberships = await context.queryClient.ensureQueryData(
			context.trpc.businesses.mine.queryOptions(),
		);

		const only = memberships.length === 1 ? memberships[0] : undefined;
		if (only !== undefined) {
			// `replace` so the back button leaves the dashboard rather than bouncing
			// through this route and landing here again.
			throw redirect({
				to: "/dashboard/$businessSlug",
				params: { businessSlug: only.business.slug },
				replace: true,
			});
		}
	},
	component: DashboardIndexPage,
});

function DashboardIndexPage() {
	const trpc = useTRPC();
	const mine = useQuery(trpc.businesses.mine.queryOptions());
	const memberships = mine.data ?? [];

	return (
		<Container size="narrow" className="py-10">
			<PageHeader
				title="Your businesses"
				description="Pick the one you want to work on."
				actions={
					memberships.length > 0 && (
						<Button asChild variant="outline">
							<Link to="/businesses/register">
								<PlusIcon className="size-4" aria-hidden="true" />
								Register another
							</Link>
						</Button>
					)
				}
			/>

			{memberships.length === 0 ? (
				<EmptyState
					className="mt-8"
					icon={BuildingIcon}
					title="No businesses yet"
					description="Register your business and a member of the Joint Development Authority will review it — usually within two business days. You can draft listings while you wait."
					action={
						<Button asChild size="lg">
							<Link to="/businesses/register">Register a business</Link>
						</Button>
					}
				/>
			) : (
				<ul className="mt-8 space-y-3">
					{memberships.map(({ business, role }) => (
						<li key={business.id}>
							<Link
								to="/dashboard/$businessSlug"
								params={{ businessSlug: business.slug }}
								className="group flex items-center gap-4 rounded-lg border bg-card p-4 transition-colors hover:border-brand/60 hover:bg-accent/40 focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2"
							>
								<BusinessAvatar
									name={business.name}
									logoUrl={business.logoUrl}
								/>

								<span className="min-w-0 flex-1">
									<span className="block truncate font-medium">
										{business.name}
									</span>
									<span className="mt-0.5 flex flex-wrap items-center gap-2">
										<span className="text-muted-foreground text-sm capitalize">
											{role}
										</span>
										{business.status !== "active" && (
											<BusinessStatusBadge status={business.status} />
										)}
									</span>
								</span>

								<ArrowRightIcon
									className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
									aria-hidden="true"
								/>
							</Link>
						</li>
					))}
				</ul>
			)}
		</Container>
	);
}
